import { useState } from "react";
import { Layout } from "@jambushrushti/components/layout/Layout";
import { useBlogsFromDB } from "@jambushrushti/hooks/useContent";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Link } from "react-router-dom";
import { Search, ArrowRight, BookOpen, Loader2 } from "lucide-react";

const Blog = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const { data: blogPosts = [], isLoading, error, refetch } = useBlogsFromDB();

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch =
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (post.excerpt || "").toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <Layout>
      {/* Hero Section */}
      <section
        className="py-5 sm:py-8 lg:py-16"
        style={{ backgroundColor: "#FAF7F2" }}
      >
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <p
              className="mb-2 text-xs font-semibold uppercase tracking-wider sm:mb-3 sm:text-sm"
              style={{ color: "#C9A227", fontFamily: "Inter, sans-serif" }}
            >
              Our Blog
            </p>
            <h1
              className="mb-3 text-3xl font-bold sm:mb-6 sm:text-5xl"
              style={{
                fontFamily: "Playfair Display, serif",
                color: "#2D2A26",
              }}
            >
              Blogs & Articles
            </h1>
            <p
              className="mb-4 text-base sm:text-lg"
              style={{ fontFamily: "Inter, sans-serif", color: "#6B6764" }}
            >
              Explore articles, tributes, and stories about Gurudev Muni
              Jambuvijayji Maharaj, his teachings, and his invaluable
              contributions to Jain scholarship.
            </p>
            <div
              className="mx-auto h-1 w-20 rounded-full"
              style={{ backgroundColor: "#C9A227" }}
            />
          </div>
        </div>
      </section>

      {/* Search */}
      <section
        className="py-3 sm:py-6"
        style={{ backgroundColor: "white", borderBottom: "1px solid #E8E4DD" }}
      >
        <div className="container mx-auto px-4">
          <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-4 md:flex-row">
            <div className="relative w-full md:w-96">
              <Search
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
                style={{ color: "#6B6764" }}
              />
              <Input
                placeholder="Search articles..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-10"
                style={{ borderColor: "#E8E4DD" }}
              />
            </div>
            <p
              className="text-sm"
              style={{ fontFamily: "Inter, sans-serif", color: "#6B6764" }}
            >
              {filteredPosts.length} articles found
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section
        className="py-6 sm:py-10 lg:py-16"
        style={{ backgroundColor: "white" }}
      >
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2
                className="h-8 w-8 animate-spin"
                style={{ color: "#C9A227" }}
              />
            </div>
          ) : error ? (
            <div role="alert" className="space-y-4 py-16 text-center">
              <p className="text-destructive">{error.message}</p>
              <Button variant="outline" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          ) : (
            <>
              <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-2 lg:grid-cols-3">
                {filteredPosts.map(post => (
                  <article
                    key={post.id}
                    className="group overflow-hidden rounded-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                    style={{
                      backgroundColor: "white",
                      border: "1px solid #E8E4DD",
                    }}
                  >
                    {/* Image */}
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={
                          post.cover_image ||
                          "https://siddhijambuparivar.com/wp-content/uploads/2022/11/96-min.jpg"
                        }
                        alt={post.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <h2
                        className="mb-3 line-clamp-2 text-xl font-bold transition-colors group-hover:opacity-80"
                        style={{
                          fontFamily: "Playfair Display, serif",
                          color: "#2D2A26",
                        }}
                      >
                        {post.title}
                      </h2>

                      <p
                        className="mb-5 line-clamp-4 text-sm leading-relaxed"
                        style={{
                          fontFamily: "Inter, sans-serif",
                          color: "#6B6764",
                        }}
                      >
                        {post.excerpt}
                      </p>

                      {/* Read More Button */}
                      <Button
                        variant="outline"
                        asChild
                        className="hover:text-white"
                        style={{ borderColor: "#C9A227", color: "#C9A227" }}
                      >
                        <Link to={`/community/blog/${post.id}`}>
                          Read More
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  </article>
                ))}
              </div>

              {filteredPosts.length === 0 && (
                <div className="mx-auto max-w-md py-16 text-center">
                  <BookOpen
                    className="mx-auto mb-4 h-16 w-16"
                    style={{ color: "#E8E4DD" }}
                  />
                  <h3
                    className="mb-2 text-xl font-semibold"
                    style={{
                      fontFamily: "Playfair Display, serif",
                      color: "#2D2A26",
                    }}
                  >
                    No articles found
                  </h3>
                  <p
                    className="mb-4"
                    style={{
                      fontFamily: "Inter, sans-serif",
                      color: "#6B6764",
                    }}
                  >
                    Try adjusting your search criteria
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setSearchQuery("")}
                    style={{ borderColor: "#C9A227", color: "#C9A227" }}
                  >
                    Clear Search
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Blog;
